import React from "react";
import { Modal, Table, Button, Alert, Typography, Divider } from "antd";
import {
  CheckCircleOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";

const { Title, Text } = Typography;

const CSVPreviewModal = ({
  visible,
  onCancel,
  onConfirm,
  csvData,
  fileName,
  loading,
}) => {
  if (!csvData || csvData.length === 0) {
    return null;
  }

  // Get headers from the first row of data
  const headers = Object.keys(csvData[0]);

  // Create columns for the table
  const columns = headers.map((header, index) => ({
    title: header,
    dataIndex: header,
    key: header,
    width: 150,
    ellipsis: true,
    render: (text) => <span title={text}>{text || "-"}</span>,
  }));

  // Add row numbers to data
  const dataWithKeys = csvData.map((row, index) => ({
    ...row,
    key: index,
    rowNumber: index + 1,
  }));

  // Add row number column
  const columnsWithRowNumber = [
    {
      title: "#",
      dataIndex: "rowNumber",
      key: "rowNumber",
      width: 50,
      fixed: "left",
    },
    ...columns,
  ];

  // Validate data
  const validateData = () => {
    const errors = [];
    const warnings = [];

    // Get available headers for debugging
    const availableHeaders = Object.keys(csvData[0] || {});
    console.log("Available CSV headers:", availableHeaders);

    // Helper function to find column by multiple possible names
    const findColumn = (row, possibleNames) => {
      for (let name of possibleNames) {
        if (row[name] !== undefined) {
          return row[name];
        }
      }
      return null;
    };

    csvData.forEach((row, index) => {
      const rowNum = index + 1;

      // Check for required fields with flexible column name matching
      const firstName = findColumn(row, [
        "First name",
        "FirstName",
        "first_name",
        "First Name",
        "FIRST NAME",
      ]);
      if (!firstName || firstName.trim() === "") {
        errors.push(`Row ${rowNum}: First name is required`);
      }

      const lastName = findColumn(row, [
        "Last name",
        "LastName",
        "last_name",
        "Last Name",
        "LAST NAME",
      ]);
      if (!lastName || lastName.trim() === "") {
        errors.push(`Row ${rowNum}: Last name is required`);
      }

      const gender = findColumn(row, [
        "Gender",
        "GENDER",
        "gender",
        "Sex",
        "SEX",
      ]);
      if (!gender || gender.trim() === "") {
        errors.push(`Row ${rowNum}: Gender is required`);
      }

      const phoneNumber = findColumn(row, [
        "Phone number",
        "PhoneNumber",
        "phone_number",
        "Phone Number",
        "PHONE NUMBER",
        "Phone",
        "PHONE",
      ]);
      if (!phoneNumber || phoneNumber.trim() === "") {
        errors.push(`Row ${rowNum}: Phone number is required`);
      }

      const poleNumber = findColumn(row, [
        "Pole Number",
        "PoleNumber",
        "pole_number",
        "Pole Number",
        "POLE NUMBER",
      ]);
      if (!poleNumber || poleNumber.trim() === "") {
        errors.push(`Row ${rowNum}: Pole Number is required`);
      }

      const branchNumber = findColumn(row, [
        "Branch Number",
        "BranchNumber",
        "branch_number",
        "Branch Number",
        "BRANCH NUMBER",
      ]);
      if (!branchNumber || branchNumber.trim() === "") {
        errors.push(`Row ${rowNum}: Branch Number is required`);
      }

      const customerType = findColumn(row, [
        "Customer Type",
        "CustomerType",
        "customer_type",
        "Customer Type",
        "CUSTOMER TYPE",
      ]);
      if (!customerType || customerType.trim() === "") {
        errors.push(`Row ${rowNum}: Customer Type is required`);
      }

      // Optional: Add validation for specific values
      const validGenders = ["Male", "Female", "Other", "M", "F"];
      if (gender && !validGenders.includes(gender.trim())) {
        warnings.push(`Row ${rowNum}: Gender should be Male, Female, or Other`);
      }

      const validCustomerTypes = ["Residential", "Commercial", "Industrial"];
      if (customerType && !validCustomerTypes.includes(customerType.trim())) {
        warnings.push(
          `Row ${rowNum}: Customer Type should be Residential, Commercial, or Industrial`
        );
      }

      // Phone number format validation
      if (phoneNumber && !/^\+?[\d\s\-\(\)]{10,}$/.test(phoneNumber.trim())) {
        warnings.push(`Row ${rowNum}: Phone number format appears invalid`);
      }
    });

    // Log missing columns for debugging
    const requiredColumns = [
      "First name",
      "Last name",
      "Gender",
      "Phone number",
      "Pole Number",
      "Branch Number",
      "Customer Type",
    ];

    const missingColumns = requiredColumns.filter(
      (col) =>
        !availableHeaders.some(
          (header) =>
            header.toLowerCase().replace(/[\s_]/g, "") ===
            col.toLowerCase().replace(/[\s_]/g, "")
        )
    );

    if (missingColumns.length > 0) {
      console.log("Missing or misnamed columns:", missingColumns);
      console.log(
        "Suggestion: Check if your CSV headers match exactly or use variations like:",
        {
          "First name": ["First name", "FirstName", "first_name"],
          "Last name": ["Last name", "LastName", "last_name"],
          Gender: ["Gender", "GENDER", "gender"],
          "Phone number": [
            "Phone number",
            "PhoneNumber",
            "phone_number",
            "Phone",
          ],
          "Pole Number": ["Pole Number", "PoleNumber", "pole_number"],
          "Branch Number": ["Branch Number", "BranchNumber", "branch_number"],
          "Customer Type": ["Customer Type", "CustomerType", "customer_type"],
        }
      );
    }

    return { errors, warnings };
  };

  const { errors, warnings } = validateData();
  const hasErrors = errors.length > 0;
  const hasWarnings = warnings.length > 0;

  return (
    <Modal
      title={
        <div className="flex items-center justify-between">
          <span>Customer Preview - {fileName}</span>
          <div className="flex items-center gap-2 text-sm">
            {/* <Text type="secondary">{csvData.length} rows</Text> */}
            {/* {!hasErrors && <CheckCircleOutlined className="text-green-500" />} */}
            {/* {hasErrors && (
              <ExclamationCircleOutlined className="text-red-500" />
            )} */}
          </div>
        </div>
      }
      open={visible}
      onCancel={onCancel}
      width={1000}
      //   style={{ top: 20 }}
      footer={[
        <Button key="cancel" onClick={onCancel}>
          Cancel
        </Button>,
        <Button
          key="confirm"
          type="primary"
          onClick={onConfirm}
          loading={loading}
          disabled={hasErrors}
        >
          {loading ? "Uploading..." : "Confirm & Upload"}
        </Button>,
      ]}
    >
      <div className="space-y-4">
        {/* Validation Alerts */}
        {hasErrors && (
          <Alert
            message="Data Validation Errors"
            description={
              <ul className="mb-0">
                {errors.slice(0, 5).map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
                {errors.length > 5 && (
                  <li>... and {errors.length - 5} more errors</li>
                )}
              </ul>
            }
            type="error"
            showIcon
          />
        )}

        {/* {hasWarnings && !hasErrors && (
          <Alert
            message="Data Validation Warnings"
            description={
              <ul className="mb-0">
                {warnings.slice(0, 5).map((warning, index) => (
                  <li key={index}>{warning}</li>
                ))}
                {warnings.length > 5 && (
                  <li>... and {warnings.length - 5} more warnings</li>
                )}
              </ul>
            }
            type="warning"
            showIcon
          />
        )} */}

        {!hasErrors && !hasWarnings && (
          <Alert
            message="Data validation successful"
            // description="All rows contain valid data and are ready for upload."
            type="success"
            showIcon
          />
        )}

        {/* <Divider /> */}

        {/* Data Preview Table */}
        <div>
          <Title level={5}>Data Preview</Title>
          <div className="border rounded">
            <Table
              columns={columnsWithRowNumber}
              dataSource={dataWithKeys}
              scroll={{ x: 800, y: 400 }}
              pagination={{
                pageSize: 10,
                showSizeChanger: false,
                showQuickJumper: true,
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} of ${total} rows`,
              }}
              size="small"
            />
          </div>
        </div>

        {/* Column Summary */}
        <div>
          <Title level={5}>Detected Columns ({headers.length})</Title>
          <div className="flex flex-wrap gap-2">
            {headers.map((header, index) => (
              <span
                key={index}
                className="px-2 py-1 bg-blue-50 text-blue-700 rounded text-sm border"
              >
                {header}
              </span>
            ))}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default CSVPreviewModal;
